import {
  ContactCandidates,
  ContactEnrichments,
  EnrichedContact,
  HUBSPOT_ONLY_PREFIX,
  candidateDisplayName,
  formatLastContacted,
  HubSpotMatches,
  PeopleRole,
  Relationship,
  relationshipFor,
  useHubSpotMatchTrace,
  useHubSpotPushContact,
  useLeadContactThread,
  useLeadOutreachThreads,
  usePeopleDiscoveryTrace,
  LeadPipelineStatus,
} from "@inrange/building-manager-api-client";
import { AITraceViewer } from "@inrange/theme-components";
import React, { useState } from "react";
import { Alert, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import CollapsibleCell from "./CollapsibleCell";
import { contactDetailsStatus } from "./leadPipelineStatus";
import { PipelineStatusLine } from "./PipelineStatusLine";
import OutreachDraftModal from "./OutreachDraftModal";
import {
  mergeContactEnrichments,
  mergeHubSpotRoleResults,
} from "./poolContacts";
import {
  CellNotice,
  ConfidenceChip,
  HubSpotSummary,
  RelationshipCell,
} from "./contactCellChrome";
import {
  ExcludedCandidatesTable,
  RankedCandidatesTable,
} from "./ContactCandidatesTables";
import { useContactsRoleDiscovery } from "./useContactsRoleDiscovery";
import { SHORTLIST_BANDS, SHORTLIST_SIZE } from "./peopleDiscoveryBands";

// How many name/position rows the collapsed cell previews before "+N more".
const PREVIEW_LIMIT = 8;

// One Contacts cell (landlord or tenant) in the leads right-panel grid, styled
// like the sibling Ownership cells. Collapsed: the company + an x/y contact
// count + a name/position preview, with the Find button in the footer. Expanded
// (full-width): the searches that ran, the ranked table + the excluded list (each
// with a select column), a "Pull contact info" action for the ticked people, and
// the AI trace. The pulled contacts land in the SEPARATE ContactDetailsCell below.
export const ContactsRoleCell: React.FC<{
  leadId: string;
  role: PeopleRole;
  decision: Record<string, unknown> | null;
  contactCandidates: ContactCandidates | null;
  contactEnrichments: ContactEnrichments | null;
  hubspotMatches: HubSpotMatches | null;
  expanded: boolean;
  onToggle: () => void;
  // Full-width row span - used when it's the sole Contacts cell (owner-occupier,
  // where only the occupier/tenant contacts are shown).
  span?: boolean;
  // Pool this cell's discovery with another role's (owner-occupier: the one company is
  // ranked under both personas, so the tenant cell pools in the landlord persona's people
  // deduped by apolloId). null = no pooling (the normal per-role cell). Required so a
  // caller can't silently forget it (see the "required typed props" rule).
  poolWithRole: PeopleRole | null;
}> = ({
  leadId,
  role,
  decision,
  contactCandidates,
  contactEnrichments,
  hubspotMatches,
  expanded,
  onToggle,
  span,
  poolWithRole,
}) => {
  const {
    companyName,
    hasCompany,
    roleResult,
    hubspotResult,
    candidates,
    eliminated,
    allCandidates,
    unranked,
    relationshipOf,
    consideredCount,
    notConsideredCount,
    searchAttempts,
    searchLogRedundant,
    throttled,
    notRun,
    truncated,
    provisional,
    enrichedIds,
    eligibilityOf,
    selectedIds,
    toggleSelect,
    selectedArr,
    freeFromHubSpot,
    chargeable,
    promotable,
    creditsLeft,
    apolloUnconfigured,
    domainInput,
    setDomainInput,
    running,
    kicking,
    discoveryPending,
    rankingInFlight,
    progressMessage,
    pullBusy,
    pullProgress,
    pullMessage,
    setPullMessage,
    pullError,
    hubSpotError,
    error,
    hubspotRefreshing,
    decideHubSpotMatch,
    handleFind,
    handleHubSpotRefresh,
    handlePull,
  } = useContactsRoleDiscovery({
    leadId,
    role,
    decision,
    contactCandidates,
    contactEnrichments,
    hubspotMatches,
    expanded,
    poolWithRole,
  });
  // Which AI trace the modal is showing (the ranking pass or the domain-resolution pass),
  // by run_id + title; null when closed. Two separate traces, one shared viewer.
  const [traceModal, setTraceModal] = useState<{
    runId: string;
    title: string;
  } | null>(null);

  // The select cell shown at the head of each people row: a "done" tick for an
  // already-pulled person (not re-selectable - re-pull is skip-only), otherwise a
  // checkbox. A legacy row without an apolloId (discovered before this shipped) is
  // simply not selectable.
  const selectCell = (apolloId: string | undefined) => {
    if (!apolloId) return <td />;
    if (enrichedIds.has(apolloId)) {
      return (
        <td
          className="text-center"
          title="Contact info already pulled"
          style={{ color: "#0f5132", fontWeight: 700 }}
        >
          ✓
        </td>
      );
    }
    const eligibility = eligibilityOf(apolloId);
    if (eligibility !== "free" && eligibility !== "chargeable") {
      // Two genuinely un-promotable cases, and they need different explanations: someone
      // who opted out (we hold an address we must not use), and someone pooled in from
      // the other role's ranking, whom the role-keyed pull would drop server-side. Shown
      // either way - not hidden - just not selectable.
      return (
        <td
          className="text-center text-muted"
          title={
            eligibility === "blocked"
              ? "Opted out of contact - cannot be promoted"
              : "Found under the landlord ranking - not pullable from the pooled view"
          }
          style={{ fontSize: 11 }}
        >
          –
        </td>
      );
    }
    return (
      <td className="text-center">
        <Form.Check
          type="checkbox"
          checked={selectedIds.has(apolloId)}
          onChange={() => toggleSelect(apolloId)}
          disabled={pullBusy}
          style={{ margin: 0, minHeight: 0 }}
          aria-label={`Select ${apolloId} to pull contact info`}
        />
      </td>
    );
  };

  const hasSelectablePeople = allCandidates.length > 0 || eliminated.length > 0;

  // Above the summary because the collapsed top line carries it now (see below).
  const belowFoldCount = eliminated.length;

  const summary = hasCompany ? (
    <div style={{ fontSize: 12.5 }}>
      <span style={{ fontWeight: 600 }}>{companyName}</span>
      {roleResult &&
        (throttled ? (
          <span style={{ color: "#b26a00", fontWeight: 600 }}>
            {" · "}
            Throttled - retry
          </span>
        ) : notRun ? (
          <span style={{ color: "#6c757d", fontWeight: 600 }}>
            {" · "}
            Not run yet - click Find people
          </span>
        ) : provisional ? (
          // A settled-looking "20/20 contacts" here would be a lie: nothing has been
          // ranked, so 20 of 20 is only "everyone Apollo returned". This cell is the
          // one the leads list shows, and it has no overlay to qualify it.
          <span style={{ color: "#6c757d" }}>
            {" · "}
            {allCandidates.length} found - not yet ranked
          </span>
        ) : (
          // A plain count of the rows listed below, not a ratio. The "N/M considered"
          // accounting belongs to the ranking and is spelled out in the expanded headline
          // beside "Show K excluded"; repeating a ratio here made this line disagree with
          // that one by exactly the HubSpot-only rows, which the preview does list. The
          // zero case keeps its own copy (the "Contacts found, none relevant" notice), so
          // nothing is lost by dropping the denominator.
          // The remainder rides on this line rather than on an advisory line of its
          // own underneath: collapsed, "1 contact" plus a separate "Only 1 top target -
          // 149 more found, expand to view." said one thing in two places and made the
          // cell taller than what it was telling you. Expanded, the full notice still
          // sits at the top of the body, where the short list it explains is on screen.
          <span style={{ color: "#6c757d" }}>
            {" · "}
            {allCandidates.length} contact
            {allCandidates.length === 1 ? "" : "s"}
            {belowFoldCount > 0 ? ` / ${belowFoldCount} more found` : ""}
          </span>
        ))}
      {/* `allCandidates`, not `candidates`: the expanded table renders ranked people PLUS
          the HubSpot-only ones, sorted so people we already know lead the list. Previewing
          the ranked list alone dropped whichever contact heads the expanded table and left
          the two views disagreeing on both membership and count. */}
      {!expanded && allCandidates.length > 0 && (
        <ul style={{ listStyle: "none", margin: "6px 0 0", padding: 0 }}>
          {allCandidates.slice(0, PREVIEW_LIMIT).map((p) => (
            <li
              key={p.apolloId}
              style={{
                display: "flex",
                gap: 8,
                padding: "3px 0",
                borderTop: "1px solid #f0f1f3",
                fontSize: 12,
              }}
            >
              <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                {candidateDisplayName(p, relationshipOf(p.apolloId))}
              </span>
              <span
                style={{
                  color: "#6c757d",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {p.title}
              </span>
            </li>
          ))}
          {allCandidates.length > PREVIEW_LIMIT && (
            <li style={{ color: "#9aa2ad", fontSize: 11.5, padding: "3px 0" }}>
              +{allCandidates.length - PREVIEW_LIMIT} more
            </li>
          )}
        </ul>
      )}
    </div>
  ) : (
    <span style={{ color: "#6c757d", fontSize: 12.5 }}>
      (choose a {role} first)
    </span>
  );

  const slotsTaken = candidates.filter(
    (c) =>
      !c.apolloId.startsWith(HUBSPOT_ONLY_PREFIX) &&
      SHORTLIST_BANDS.includes(c.band ?? "")
  ).length;
  // Only drives whether this advisory line renders, so a drift in the constants above is
  // cosmetic - it can never change who is shortlisted.
  const shortOfTargets =
    roleResult != null && slotsTaken < SHORTLIST_SIZE && belowFoldCount > 0;

  // Expanded body only, at the top, where it explains the short list sitting right
  // under it. Collapsed, the summary line above carries the one fact that mattered
  // there ("/ N more found") - the shortfall wording adds nothing you can act on until
  // the remainder list is actually on screen.
  const shortlistShortfallNotice = shortOfTargets ? (
    <div style={{ fontSize: 12, color: "#664d03", marginBottom: 6 }}>
      {slotsTaken === 0
        ? `No top targets - ${belowFoldCount} other contact${belowFoldCount === 1 ? "" : "s"} found`
        : `Only ${slotsTaken} top target${slotsTaken === 1 ? "" : "s"} - ${belowFoldCount} more found`}
      , listed below, best first.
    </div>
  ) : null;

  const footer = (
    <>
      <div
        className="d-flex align-items-center gap-2 flex-wrap"
        style={{ marginTop: 8 }}
      >
        {/* Expanded only. It is an override for the rare case where the name
            search misses, so on a collapsed cell it is a wide empty box sitting
            between the summary and the button for no reason. The Find button
            stays, so the value is CLEARED on collapse (see the effect above)
            rather than merely hidden - a domain the operator cannot see must
            never silently narrow the next search. */}
        {hasCompany && expanded && (
          <Form.Control
            type="text"
            size="sm"
            value={domainInput}
            onChange={(e) => setDomainInput(e.target.value)}
            placeholder="optional domain (e.g. company.co.uk)"
            disabled={running || kicking}
            style={{ maxWidth: 240, fontSize: 12 }}
          />
        )}
        <Button
          size="sm"
          variant="outline-primary"
          disabled={
            !hasCompany ||
            apolloUnconfigured ||
            running ||
            kicking ||
            discoveryPending
          }
          onClick={() => void handleFind()}
        >
          {running || kicking || discoveryPending ? (
            <>
              <Spinner size="sm" animation="border" className="me-1" />
              {progressMessage ??
                (running || kicking ? "Finding people..." : "Checking...")}
            </>
          ) : roleResult && !notRun ? (
            "Re-find people (free)"
          ) : (
            "Find people (free)"
          )}
        </Button>
        {discoveryPending && !running && !kicking && (
          // The button itself carries the spinner + disabled state, so this is a plain
          // explanation of what it is waiting for rather than a second spinner beside an
          // enabled button (which read as "idle" and invited a click that raced the
          // auto-kicked run).
          <span className="text-muted" style={{ fontSize: 12 }}>
            Checking for an automatic contacts run...
          </span>
        )}
        {apolloUnconfigured && !running && !kicking && (
          // The environment has no Apollo key, so nothing will ever run here. Say so
          // rather than resting on an empty cell the operator reads as "still loading".
          <span style={{ fontSize: 12, color: "#664d03" }}>
            Apollo isn&apos;t configured on this environment, so no contacts can
            be found.
          </span>
        )}
      </div>
      {error && <CellNotice>{error}</CellNotice>}
    </>
  );

  return (
    <>
      <CollapsibleCell
        title={role}
        summary={summary}
        footer={footer}
        expanded={expanded}
        onToggle={onToggle}
        span={span}
      >
        {roleResult ? (
          <>
            {shortlistShortfallNotice}
            <div
              className="text-muted d-flex align-items-center gap-2 flex-wrap"
              style={{ fontSize: 12 }}
            >
              <span>
                {throttled ? (
                  <span style={{ color: "#b26a00", fontWeight: 600 }}>
                    Throttled at{" "}
                    {new Date(roleResult.discoveredAt).toLocaleString()}
                  </span>
                ) : notRun ? (
                  <span style={{ color: "#6c757d", fontWeight: 600 }}>
                    Not run yet - the bulk contacts run didn&apos;t reach this
                    one
                  </span>
                ) : truncated ? (
                  <span style={{ color: "#b26a00", fontWeight: 600 }}>
                    Cut off at{" "}
                    {new Date(roleResult.discoveredAt).toLocaleString()}
                  </span>
                ) : provisional ? (
                  // "N people from N considered" would read as a finished ranking that
                  // kept everyone. Nothing has been considered yet - these are just
                  // everyone Apollo and HubSpot returned.
                  <>
                    {candidates.length} people found at {roleResult.companyName}
                    {roleResult.sampled && " (sampled)"} -{" "}
                    {rankingInFlight ? "ranking them now" : "not yet ranked"}
                  </>
                ) : (
                  <>
                    {candidates.length} people from {consideredCount}
                    {notConsideredCount > 0
                      ? ` considered of ${roleResult.apolloTotalEntries}`
                      : ""}{" "}
                    at {roleResult.companyName}
                    {/* Our own cut, not Apollo's sampling: these people are in neither
                        list, so naming them is what stops the counts above reading as
                        the whole population. */}
                    {(roleResult.discardedCount ?? 0) > 0 &&
                      ` (${roleResult.discardedCount} lower-priority titles not ranked)`}
                    {roleResult.sampled && " (sampled)"} -{" "}
                    {new Date(roleResult.discoveredAt).toLocaleString()}
                  </>
                )}
              </span>
              {/* Only offer the button when a ranking trace exists (its run_id is on the
                  role blob). A throttle / not_run marker / no_people run stores none. */}
              {!throttled && !notRun && roleResult.traceRunId && (
                <Button
                  variant="link"
                  className="p-0"
                  style={{ fontSize: 12, verticalAlign: "baseline" }}
                  onClick={() =>
                    setTraceModal({
                      runId: roleResult.traceRunId!,
                      title: `Contact ranking - AI trace (${role})`,
                    })
                  }
                >
                  view contact ranking trace
                </Button>
              )}
            </div>

            {roleResult.domain && (
              <div className="mt-1" style={{ fontSize: 12 }}>
                <span style={{ color: "#0a7d55", fontWeight: 600 }}>
                  Domain used: {roleResult.domain}
                </span>
                {roleResult.domainSource === "ai" && (
                  <span className="text-muted">
                    {" (found by AI"}
                    {roleResult.domainTraceRunId && (
                      <>
                        {" - "}
                        <Button
                          variant="link"
                          className="p-0"
                          style={{ fontSize: 12, verticalAlign: "baseline" }}
                          onClick={() =>
                            setTraceModal({
                              runId: roleResult.domainTraceRunId!,
                              title: `Domain resolution - AI trace (${role})`,
                            })
                          }
                        >
                          view trace
                        </Button>
                      </>
                    )}
                    {")"}
                  </span>
                )}
                {roleResult.domainSource === "captured" && (
                  <span className="text-muted">
                    {" "}
                    (from the website on file)
                  </span>
                )}
                {roleResult.domainSource === "override" && (
                  <span className="text-muted"> (you entered this)</span>
                )}
              </div>
            )}

            {/* The AI domain resolver ran but adopted no domain (it wasn't confident, so
                Apollo fell back to a name search). No domain to show, but the pass left a
                trace - surface it so the operator can see WHY it gave up, rather than the
                button silently vanishing as it did before. */}
            {!roleResult.domain && roleResult.domainTraceRunId && (
              <div className="mt-1" style={{ fontSize: 12 }}>
                <span className="text-muted">
                  AI domain search found no confident domain - searched by name.{" "}
                  <Button
                    variant="link"
                    className="p-0"
                    style={{ fontSize: 12, verticalAlign: "baseline" }}
                    onClick={() =>
                      setTraceModal({
                        runId: roleResult.domainTraceRunId!,
                        title: `Domain resolution - AI trace (${role}, no domain found)`,
                      })
                    }
                  >
                    view trace
                  </Button>
                </span>
              </div>
            )}

            <HubSpotSummary
              hubspot={hubspotResult}
              busy={hubspotRefreshing}
              onRefresh={() => void handleHubSpotRefresh()}
              onViewTrace={() =>
                hubspotResult?.traceRunId
                  ? setTraceModal({
                      runId: hubspotResult.traceRunId,
                      title: "HubSpot matching",
                    })
                  : undefined
              }
            />

            {searchAttempts.length > 0 && !searchLogRedundant && (
              <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                {searchAttempts.length > 1 ? "Searches run: " : "Searched: "}
                {searchAttempts
                  .map((a) =>
                    a.kind === "domain"
                      ? `domain ${a.query}: ${a.totalEntries}`
                      : `name "${a.query}": ${a.totalEntries}`
                  )
                  .join(" · ")}
              </div>
            )}

            {allCandidates.length === 0 ? (
              <div className="text-muted mt-1" style={{ fontSize: 13 }}>
                {/* Nothing to overlay yet - Apollo hasn't come back - so the running
                    state has to be said here instead, or the panel reads as a
                    finished search that found nobody. */}
                {rankingInFlight ? (
                  <>
                    <Spinner
                      animation="border"
                      size="sm"
                      className="me-2 align-middle"
                    />
                    {progressMessage ?? "Finding people..."}
                  </>
                ) : throttled ? (
                  "Apollo's daily discovery quota was used up when this ran - retry to search again."
                ) : notRun ? (
                  'The bulk contacts run didn’t reach this one - click "Find people (free)" below to run it now.'
                ) : truncated ? (
                  "The AI ranking was cut off before it finished, so no list came back - re-run to try again. Nobody has been ruled out."
                ) : (
                  "No people to show."
                )}
              </div>
            ) : (
              <RankedCandidatesTable
                candidates={allCandidates}
                unranked={unranked}
                provisional={provisional}
                rankingInFlight={rankingInFlight}
                progressMessage={progressMessage}
                relationshipOf={relationshipOf}
                renderSelectCell={selectCell}
                onDecideMatch={(apolloId, contactId, verdict) =>
                  decideHubSpotMatch.mutate({
                    leadId,
                    role,
                    apolloId,
                    contactId,
                    verdict,
                  })
                }
                decisionBusy={decideHubSpotMatch.isLoading}
              />
            )}

            <ExcludedCandidatesTable
              eliminated={eliminated}
              relationshipOf={relationshipOf}
              renderSelectCell={selectCell}
            />

            {/* Pull action - BELOW both tables so it clearly applies to everyone
                ticked (ranked + excluded). Reveals email/title/LinkedIn for the new
                selections; already-pulled rows are skipped (no re-charge). */}
            {hasSelectablePeople && (
              <div
                className="d-flex align-items-center gap-2 flex-wrap"
                style={{ marginTop: 10 }}
              >
                <Button
                  size="sm"
                  variant="outline-primary"
                  disabled={promotable === 0 || pullBusy}
                  onClick={() => void handlePull()}
                >
                  {pullBusy ? (
                    <>
                      <Spinner size="sm" animation="border" className="me-1" />
                      {pullProgress ?? "Promoting..."}
                    </>
                  ) : (
                    "Promote to contact list"
                  )}
                </Button>
                {!pullBusy && promotable > 0 && (
                  <span className="text-muted" style={{ fontSize: 12 }}>
                    {selectedArr.length} selected
                    {freeFromHubSpot.length > 0 && (
                      <>
                        {" · "}
                        <span style={{ color: "#0f5132", fontWeight: 600 }}>
                          {freeFromHubSpot.length} free from HubSpot
                        </span>
                      </>
                    )}
                    {chargeable.length > 0 ? (
                      <>
                        {" · "}
                        <span style={{ color: "#212529", fontWeight: 600 }}>
                          {chargeable.length} paid
                        </span>
                        {", using "}
                        {chargeable.length}
                        {creditsLeft != null
                          ? ` of remaining ${creditsLeft}`
                          : ""}{" "}
                        Apollo credit{chargeable.length === 1 ? "" : "s"}
                      </>
                    ) : (
                      " · no Apollo credits needed"
                    )}
                  </span>
                )}
                {!pullBusy &&
                  chargeable.length === 0 &&
                  selectedArr.length > 0 && (
                    <span className="text-muted" style={{ fontSize: 12 }}>
                      all selected already pulled
                    </span>
                  )}
                {/* Success confirmation: small + inline (right of the button), and
                    dismissable. It's cleared at the start of the next pull, so a fresh
                    run always overwrites the previous one. */}
                {!pullBusy && pullMessage && (
                  <span
                    className="d-inline-flex align-items-center"
                    style={{ fontSize: 12, color: "#0f5132" }}
                  >
                    {pullMessage}
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 ms-1"
                      style={{
                        fontSize: 14,
                        lineHeight: 1,
                        color: "#9aa2ad",
                        textDecoration: "none",
                      }}
                      onClick={() => setPullMessage(null)}
                      title="Dismiss"
                      aria-label="Dismiss confirmation"
                    >
                      ×
                    </Button>
                  </span>
                )}
              </div>
            )}
            {pullError && <CellNotice>{pullError}</CellNotice>}
            {hubSpotError && <CellNotice>{hubSpotError}</CellNotice>}
          </>
        ) : (
          <div className="text-muted" style={{ fontSize: 13 }}>
            No contacts yet - use &quot;Find people (free)&quot; below.
          </div>
        )}
      </CollapsibleCell>

      <PeopleTraceModal
        leadId={leadId}
        role={role}
        openTrace={traceModal}
        onHide={() => setTraceModal(null)}
      />
    </>
  );
};

// The expanded row in the contact list: everything HubSpot holds about this person,
// which is what a warm draft is written from. Rendered in place rather than as a popup
// so it reads as part of the list it belongs to.
const ContactRelationshipDetail: React.FC<{
  relationship: Relationship;
  onViewThread: () => void;
}> = ({ relationship, onViewThread }) => {
  const contact = relationship.contact;
  if (!contact) return null;
  const row = (label: string, value: React.ReactNode) => (
    <>
      <div style={{ color: "#6c757d" }}>{label}</div>
      <div>{value}</div>
    </>
  );
  return (
    <div style={{ padding: "10px 4px 12px" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "150px minmax(0, 1fr)",
          gap: "7px 14px",
          fontSize: 12.5,
        }}
      >
        {contact.email && row("Email held", contact.email)}
        {contact.ownerId && row("Owner", contact.ownerId)}
        {(contact.lifecycleStage || contact.leadStatus) &&
          row(
            "Where they stand",
            [contact.lifecycleStage, contact.leadStatus]
              .filter(Boolean)
              .join(" · ")
          )}
        {contact.lastContacted &&
          row(
            "Last contacted",
            <span>
              {formatLastContacted(contact.lastContacted)}{" "}
              <Button
                variant="link"
                className="p-0"
                style={{ fontSize: 12, verticalAlign: "baseline" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onViewThread();
                }}
              >
                View thread
              </Button>
            </span>
          )}
        {contact.noteCount != null &&
          row("Logged activity", `${contact.noteCount} notes`)}
        {relationship.kind === "moved" &&
          contact.companyName &&
          row(
            "We know them from",
            `${contact.companyName} - the address held there is not reused`
          )}
        {relationship.match?.reasoning &&
          row("Matched by", relationship.match.reasoning)}
        {relationship.decision &&
          row(
            "Confirmed by",
            `${relationship.decision.decidedBy} (${relationship.decision.verdict})`
          )}
      </div>
    </div>
  );
};

// The whole email history HubSpot holds for one contact, newest first. Read live when
// opened. An empty list on a linked contact means nothing is LOGGED in HubSpot, which is
// not the same as never contacted - so it says which rather than implying a clean slate.
const ContactThreadModal: React.FC<{
  show: boolean;
  onHide: () => void;
  leadId: string;
  role: PeopleRole;
  contact: EnrichedContact | null;
}> = ({ show, onHide, leadId, role, contact }) => {
  const thread = useLeadContactThread(
    leadId,
    role,
    contact?.apolloId ?? null,
    show
  );
  const emails = thread.data?.emails ?? [];
  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 17 }}>
          Email thread
          <span
            className="text-muted"
            style={{ fontSize: 13, marginLeft: 8, fontWeight: 400 }}
          >
            {contact?.name} · from HubSpot
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {thread.isLoading ? (
          <div className="text-muted" style={{ fontSize: 13 }}>
            <Spinner size="sm" animation="border" className="me-2" />
            Reading HubSpot...
          </div>
        ) : thread.data?.error ? (
          <Alert variant="warning" className="mb-0" style={{ fontSize: 13 }}>
            Could not read the thread from HubSpot: {thread.data.error}
          </Alert>
        ) : emails.length === 0 ? (
          <div className="text-muted" style={{ fontSize: 13 }}>
            {thread.data?.linked === false
              ? "This contact is not linked to a HubSpot record."
              : "Nothing is logged against this contact in HubSpot. That is not the same as never contacted - an email sent from Gmail without HubSpot logging leaves no trace here."}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {emails.map((email, index) => (
              <div
                key={email.emailId}
                style={{
                  border: "1px solid #dee2e6",
                  borderLeft:
                    index === 0 ? "3px solid #0d6efd" : "1px solid #dee2e6",
                  borderRadius: 6,
                  padding: "10px 12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: 12.5 }}>
                    {email.inbound ? "They wrote" : "We wrote"}
                  </span>
                  <span style={{ flexGrow: 1 }} />
                  <span className="text-muted" style={{ fontSize: 12 }}>
                    {email.sentAt ?? "no date"}
                  </span>
                </div>
                {email.subject && (
                  <div
                    style={{ fontWeight: 600, fontSize: 12.5, marginTop: 6 }}
                  >
                    {email.subject}
                  </div>
                )}
                {email.body && (
                  <div
                    style={{
                      fontSize: 12.5,
                      lineHeight: 1.55,
                      marginTop: 5,
                      color: "#495057",
                      whiteSpace: "pre-line",
                    }}
                  >
                    {email.body}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

// The pulled-contacts tile: a SEPARATE cell that sits beneath its role's people
// cell (tenant under tenant, landlord under landlord). Lists the revealed contacts -
// Name (links to LinkedIn) / Title / Email / Confidence chip - each with a disabled
// "Draft email" button (wired up in a future pass). Empty until the first pull.
// Offered only when Apollo filled a field HubSpot had blank, so the button never
// proposes writing HubSpot's own data back to it. The write itself re-checks each field
// is still empty, so a colleague filling it in first always wins.
const PushToHubSpotButton: React.FC<{
  leadId: string;
  role: PeopleRole;
  contact: EnrichedContact;
}> = ({ leadId, role, contact }) => {
  const push = useHubSpotPushContact(leadId);
  const filled = Object.keys(contact.apolloFilled ?? {});
  if (!contact.hubspotContactId || filled.length === 0) return null;
  if (contact.hubspotPushedAt) {
    return (
      <span
        className="text-muted"
        style={{ marginLeft: 5, fontSize: 11 }}
        title={`Pushed ${new Date(contact.hubspotPushedAt).toLocaleString()}`}
      >
        In HubSpot ✓
      </span>
    );
  }
  return (
    <>
      <Button
        size="sm"
        variant="outline-secondary"
        disabled={push.isLoading}
        onClick={() => push.mutate({ role, apolloId: contact.apolloId })}
        title={`Write ${filled.join(", ")} into this HubSpot contact - only fills fields HubSpot has blank`}
        style={{ whiteSpace: "nowrap", fontSize: 11, marginLeft: 5 }}
      >
        {push.isLoading ? "Pushing..." : "Push to HubSpot"}
      </Button>
      {push.isError && (
        <div style={{ color: "#842029", fontSize: 11, marginTop: 3 }}>
          {(push.error as { response?: { data?: { error?: string } } })
            ?.response?.data?.error ?? "Could not write to HubSpot."}
        </div>
      )}
    </>
  );
};

export const ContactDetailsCell: React.FC<{
  leadId: string;
  role: PeopleRole;
  contactEnrichments: ContactEnrichments | null;
  hubspotMatches: HubSpotMatches | null;
  expanded: boolean;
  onToggle: () => void;
  // Full-width row span - used when it's the sole details cell (owner-occupier).
  span?: boolean;
  // Pool this cell's pulled contacts with another role's (owner-occupier), deduped by
  // apolloId - mirrors ContactsRoleCell so the done-ticks above and the details below
  // always agree. null = no pooling. Required (see the "required typed props" rule).
  poolWithRole: PeopleRole | null;
  // The lead-level stage, so the empty state can say WHY nothing is here. "No contacts
  // pulled yet" is true of a pull still running, one that stopped part-way and one that
  // never started, and the operator cannot tell which - which is exactly how a chain
  // that failed to launch read as an ordinary empty result. Required, and null on the
  // surfaces that have no status to give it. Filtered through `contactDetailsStatus`
  // before it renders: the status is LEAD-level, so not all of it is true of one role's
  // details box.
  pipelineStatus: LeadPipelineStatus | null;
  // Whether this cell's role has a company chosen. Without one the chain is not working
  // on this role at all, so the lead-level status must not be repeated here - a landlord
  // box reading "Finding contacts" while the row still says "choose a landlord first"
  // is the symptom. Required, not defaulted: a caller that forgot would silently claim
  // work that is not happening.
  hasCompany: boolean;
  // Apollo credits already spent on this lead, keyed by role. Rendered because the
  // chain now pulls contact details automatically on promote: the figure used to
  // appear in the message after clicking "Promote to contact list", and nobody clicks
  // that any more, so real money was being spent with nothing on screen saying so.
  // Required, and null on a surface with no figure to give.
  apolloCredits: Record<string, number> | null;
}> = ({
  leadId,
  role,
  contactEnrichments,
  hubspotMatches,
  expanded,
  onToggle,
  span,
  poolWithRole,
  pipelineStatus,
  hasCompany,
  apolloCredits,
}) => {
  const enrichResult = mergeContactEnrichments(
    contactEnrichments?.[role] ?? null,
    poolWithRole ? (contactEnrichments?.[poolWithRole] ?? null) : null
  );
  const contacts: EnrichedContact[] = enrichResult?.contacts ?? [];
  const companyName = enrichResult?.companyName ?? null;
  // The contact the operator is drafting an outreach email for (opens the studio).
  const [draftFor, setDraftFor] = useState<EnrichedContact | null>(null);
  // Which row is expanded, and whether its thread dialog is open. Expansion lives HERE
  // and not in the candidates cell: drafting happens in this list, so all the history a
  // draft is written from is read in one place rather than two.
  const [expandedContact, setExpandedContact] = useState<string | null>(null);
  const [threadFor, setThreadFor] = useState<EnrichedContact | null>(null);
  const hubspotResult = mergeHubSpotRoleResults(
    hubspotMatches?.[role] ?? null,
    poolWithRole ? (hubspotMatches?.[poolWithRole] ?? null) : null
  );
  const relationshipOf = (apolloId: string): Relationship =>
    relationshipFor(apolloId, hubspotResult, {
      hubspotOnly: apolloId.startsWith(HUBSPOT_ONLY_PREFIX),
    });
  // Existing draft threads for this lead, so a contact with a draft shows a "v{turns}"
  // badge on its Draft email button when expanded and an "Email drafted" tag beside the
  // name when collapsed. Key on THIS cell's role as well as the contact - the modal
  // decides load-vs-draft on (role, apolloId), so a same-apolloId thread under the OTHER
  // role must not badge this cell as "v{n}" (it would open and draft a fresh thread,
  // contradicting the badge).
  //
  // Fetched while collapsed too, which it previously was not: the collapsed preview is
  // where an operator looks to see whether a lead is DONE, and that is exactly the state
  // the automatic chain now leaves behind without anyone opening the cell. One request
  // per lead either way - both role cells share the ["leadOutreachThreads", leadId] key,
  // so React Query serves the second from the first - and it is skipped entirely on a
  // cell with nothing pulled, which has no name to tag.
  const pulled = contacts.filter((c) => c.matched);
  const threadsQuery = useLeadOutreachThreads(
    leadId,
    expanded || pulled.length > 0
  );
  const turnsByApollo = new Map<string, number>();
  for (const t of threadsQuery.data ?? []) {
    if (t.role === role && t.contactApolloId) {
      turnsByApollo.set(t.contactApolloId, t.turnCount);
    }
  }
  // Every ticked person gets a record (matched or missed) so skip-only can re-skip it,
  // so `attempted` = how many we tried, `withEmail` = how many came back with an email,
  // and `pulled` = the ones Apollo actually matched (what the preview lists).
  const attempted = contacts.length;
  const withEmail = contacts.filter((c) => !!c.email).length;
  // Pooled the same way the contacts above are, so an owner-occupier cell showing both
  // personas' people also shows what both personas cost. A pull that spent nothing -
  // everyone already known to HubSpot - has no entry and renders no line.
  const creditsSpent =
    (apolloCredits?.[role] ?? 0) +
    (poolWithRole ? (apolloCredits?.[poolWithRole] ?? 0) : 0);

  // Mirror the people tile above: company + a "N/M" count, then a name/title preview
  // when collapsed. The full detail table lives in the expanded body below.
  const summary =
    attempted > 0 ? (
      <div style={{ fontSize: 12.5 }}>
        {companyName && <span style={{ fontWeight: 600 }}>{companyName}</span>}
        <span style={{ color: "#6c757d" }}>
          {companyName ? " · " : ""}
          {withEmail}/{attempted} with email
        </span>
        {creditsSpent > 0 && (
          <span style={{ color: "#6c757d" }}>
            {" · "}
            {/* Rounded, not raw: Apollo returns a decimal and a "2.0000000001
                credits" would read as a bug rather than a price. */}
            {Math.round(creditsSpent * 100) / 100} Apollo credit
            {creditsSpent === 1 ? "" : "s"}
          </span>
        )}
        {!expanded && pulled.length > 0 && (
          <ul style={{ listStyle: "none", margin: "6px 0 0", padding: 0 }}>
            {pulled.slice(0, PREVIEW_LIMIT).map((c) => (
              <li
                key={c.apolloId}
                style={{
                  display: "flex",
                  gap: 8,
                  padding: "3px 0",
                  borderTop: "1px solid #f0f1f3",
                  fontSize: 12,
                  alignItems: "baseline",
                }}
              >
                <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                  {c.name}
                </span>
                {/* Wraps rather than ellipsing: a long title is worth reading, and
                    truncating it is what pushed the interesting half out of sight. */}
                <span style={{ color: "#6c757d", minWidth: 0 }}>{c.title}</span>
                {turnsByApollo.has(c.apolloId) && (
                  // Last in the row, after the title: it is the END state of the chain,
                  // so it reads as the outcome of the person rather than an adjective
                  // in front of their job. Not a count either - "v2" is a drafting
                  // detail for the button inside; out here the only question is whether
                  // this person has an email waiting.
                  //
                  // `marginLeft: auto` pins it to the right edge, so the tag lines up
                  // down the list instead of landing at a different x on every row, and
                  // `nowrap` keeps it on one line however far the title wraps.
                  <span
                    style={{
                      color: "#0f5132",
                      whiteSpace: "nowrap",
                      fontWeight: 600,
                      marginLeft: "auto",
                    }}
                  >
                    Email drafted
                  </span>
                )}
              </li>
            ))}
            {pulled.length > PREVIEW_LIMIT && (
              <li
                style={{ color: "#9aa2ad", fontSize: 11.5, padding: "3px 0" }}
              >
                +{pulled.length - PREVIEW_LIMIT} more
              </li>
            )}
          </ul>
        )}
      </div>
    ) : (
      // Same derivation and wording as the Status column, so the two cannot disagree
      // about where this lead got to - but only the part of it that is about contact
      // details, and only when this role has a company at all. The empty-state variant
      // owns the no-status sentence, so a filtered-out status falls back to "No
      // contacts pulled yet" rather than rendering blank.
      <PipelineStatusLine
        status={contactDetailsStatus(pipelineStatus, hasCompany)}
        variant="empty-state"
      />
    );

  return (
    <CollapsibleCell
      title={`${role} · contact details`}
      summary={summary}
      expanded={expanded}
      onToggle={onToggle}
      span={span}
    >
      {contacts.length === 0 ? (
        <div className="text-muted" style={{ fontSize: 13 }}>
          Tick people in the {role} tile above, then use &quot;Pull contact
          info&quot;.
        </div>
      ) : (
        <Table
          size="sm"
          className="mt-1 mb-1 align-middle"
          style={{ fontSize: 12 }}
        >
          <thead>
            <tr className="text-muted">
              <th style={{ width: 18 }} />
              <th>Name</th>
              <th>Title</th>
              <th>Email</th>
              <th style={{ width: 176 }}>Relationship</th>
              <th>Confidence</th>
              <th style={{ width: 1 }} />
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => {
              const relationship = relationshipOf(c.apolloId);
              const known = relationship.kind !== "apollo_only";
              const isExpanded = expandedContact === c.apolloId;
              return (
                <React.Fragment key={c.apolloId}>
                  <tr
                    onClick={() =>
                      known
                        ? setExpandedContact(isExpanded ? null : c.apolloId)
                        : undefined
                    }
                    style={known ? { cursor: "pointer" } : undefined}
                  >
                    <td>
                      {known && (
                        <span style={{ color: "#6c757d", fontSize: 11 }}>
                          {isExpanded ? "\u25be" : "\u25b8"}
                        </span>
                      )}
                    </td>
                    <td className="fw-semibold text-nowrap">
                      {c.linkedinUrl ? (
                        <a
                          href={c.linkedinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open LinkedIn profile"
                        >
                          {c.name}
                          <span
                            style={{
                              fontSize: 8.5,
                              fontWeight: 700,
                              color: "#fff",
                              background: "#0a66c2",
                              borderRadius: 3,
                              padding: "0 3px",
                              marginLeft: 5,
                              verticalAlign: "middle",
                            }}
                          >
                            in
                          </span>
                        </a>
                      ) : (
                        c.name
                      )}
                    </td>
                    <td>{c.title || "-"}</td>
                    <td>
                      {c.email ? (
                        <span style={{ fontVariantNumeric: "tabular-nums" }}>
                          {c.email}
                        </span>
                      ) : (
                        <span className="text-muted">- no email found</span>
                      )}
                    </td>
                    <td>
                      <RelationshipCell relationship={relationship} />
                    </td>
                    <td>
                      {c.emailSource === "hubspot" ? (
                        // An address HubSpot gave us never went through Apollo's
                        // verification, so it carries its own source rather than borrowing a
                        // confidence word that would misdescribe where it came from.
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            fontSize: 11,
                            fontWeight: 600,
                            borderRadius: 999,
                            padding: "2px 9px",
                            whiteSpace: "nowrap",
                            background: "#d1e7dd",
                            color: "#0f5132",
                          }}
                        >
                          From HubSpot
                        </span>
                      ) : (
                        <ConfidenceChip contact={c} />
                      )}
                    </td>
                    <td className="text-end">
                      <Button
                        size="sm"
                        variant={
                          turnsByApollo.has(c.apolloId)
                            ? "primary"
                            : "outline-primary"
                        }
                        onClick={() => setDraftFor(c)}
                        title={
                          turnsByApollo.has(c.apolloId)
                            ? "A draft already exists for this contact - open to view or rerun"
                            : "Draft a cold-outreach email to this contact"
                        }
                        style={{ whiteSpace: "nowrap", fontSize: 11 }}
                      >
                        Draft email
                        {turnsByApollo.has(c.apolloId) && (
                          <span
                            style={{
                              marginLeft: 5,
                              fontSize: 9.5,
                              fontWeight: 700,
                              background: "#fff",
                              color: "#0a58ca",
                              borderRadius: 3,
                              padding: "0 4px",
                              verticalAlign: "middle",
                            }}
                          >
                            v{turnsByApollo.get(c.apolloId)}
                          </span>
                        )}
                      </Button>
                      <PushToHubSpotButton
                        leadId={leadId}
                        role={role}
                        contact={c}
                      />
                      {known && relationship.kind !== "do_not_contact" && (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            marginLeft: 5,
                            fontSize: 11,
                            fontWeight: 600,
                            borderRadius: 999,
                            padding: "1px 8px",
                            background:
                              relationship.kind === "moved"
                                ? "#cfe2ff"
                                : "#d1e7dd",
                            color:
                              relationship.kind === "moved"
                                ? "#084298"
                                : "#0f5132",
                          }}
                          title="We have contacted this person before - the draft opens warm"
                        >
                          Warm
                        </span>
                      )}
                    </td>
                  </tr>
                  {isExpanded && relationship.contact && (
                    <tr>
                      <td colSpan={7} style={{ background: "#f6fbf8" }}>
                        <ContactRelationshipDetail
                          relationship={relationship}
                          onViewThread={() => setThreadFor(c)}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      )}
      <ContactThreadModal
        show={!!threadFor}
        onHide={() => setThreadFor(null)}
        leadId={leadId}
        role={role}
        contact={threadFor}
      />
      <OutreachDraftModal
        show={!!draftFor}
        onHide={() => setDraftFor(null)}
        leadId={leadId}
        role={role}
        contact={draftFor}
        companyName={companyName}
        onViewThread={() => setThreadFor(draftFor)}
      />
    </CollapsibleCell>
  );
};

const PeopleTraceModal: React.FC<{
  leadId: string;
  role: PeopleRole;
  // The trace to show (run_id + modal title), or null when closed. One viewer serves
  // both the contact-ranking trace and the domain-resolution trace.
  openTrace: { runId: string; title: string } | null;
  onHide: () => void;
}> = ({ leadId, role, openTrace, onHide }) => {
  // The two lead-AI surfaces this cell can open store under different trace prefixes, so
  // the fetch is routed by which one was asked for rather than a second modal component.
  const isHubSpotTrace = openTrace?.title === "HubSpot matching";
  const peopleTrace = usePeopleDiscoveryTrace(
    `${leadId}:${role}`,
    openTrace?.runId ?? null,
    openTrace !== null && !isHubSpotTrace
  );
  const hubspotTrace = useHubSpotMatchTrace(
    `${leadId}:${role}`,
    openTrace?.runId ?? null,
    openTrace !== null && isHubSpotTrace
  );
  const traceQuery = isHubSpotTrace ? hubspotTrace : peopleTrace;
  return (
    <Modal show={openTrace !== null} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{openTrace?.title ?? "AI trace"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {traceQuery.isLoading ? (
          <Spinner animation="border" />
        ) : traceQuery.data ? (
          <AITraceViewer trace={traceQuery.data} />
        ) : (
          <div className="text-muted">No AI trace available.</div>
        )}
      </Modal.Body>
    </Modal>
  );
};
